import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button, Chip, Labeled, Modal, Problem } from "./index.tsx";

/**
 * The composer: a template, a subject, a message, the merge chips, and the
 * preview as the recipient reads it. The person who writes the message and
 * presses Send is the human in "nothing reaches a customer or a vendor
 * unreviewed" (James, 2026-09-01, T20).
 *
 * Here rather than in a module because two screens draw it: a customer record
 * in Sales and a vendor's page in Vendors, and modules may never import each
 * other. It is presentational and structural on purpose, the Checklist
 * precedent: the caller brings the templates, the merge rendering, the blank
 * detection and the send, so this package keeps no dependency it would
 * otherwise not need. It decides nothing about who may be written to.
 */

export interface ComposeTemplate { id: string; name: string; subject: string | null; body: string | null }
export interface ComposeChip { label: string; tag: string }

export interface ComposeModalProps {
  open: boolean;
  channel: "email" | "sms";
  title: string;
  hint?: string;
  /** Who it goes to, and where. A null address disables Send with the reason on screen. */
  to: { name: string; address: string | null } | null;
  /** One line under the recipient: the deal, or what is expiring. */
  contextLine?: ReactNode;
  templates: readonly ComposeTemplate[];
  /** Preselected on open, when the caller has one in mind (Request COI). */
  initialTemplateId?: string | null;
  chips: readonly ComposeChip[];
  /** Merge fields filled in, from the caller's context. */
  render: (text: string) => string;
  /** The merge fields in the text that would render blank. A blank blocks Send. */
  blanksIn: (text: string) => string[];
  /** SMS only: how many segments the rendered body costs. */
  segmentsOf?: (rendered: string) => number;
  onSend: (m: { subject: string; body: string; templateId: string | null }) => void;
  sending: boolean;
  error: unknown;
  onClose: () => void;
  /** Anything else the caller wants under the message: a picker, a note. */
  children?: ReactNode;
}

const field: React.CSSProperties = {
  padding: "7px 10px", borderRadius: "var(--r-ctl)", border: "1px solid var(--hairline)",
  background: "var(--card)", color: "var(--ink)", fontFamily: "inherit", fontSize: 13, minWidth: 0, width: "100%",
};
const textArea: React.CSSProperties = { ...field, lineHeight: 1.45, resize: "vertical" };

export function ComposeModal(p: ComposeModalProps) {
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const pick = (id: string | null) => {
    const t = id ? p.templates.find((x) => x.id === id) ?? null : null;
    setTemplateId(t ? t.id : null);
    setSubject(t?.subject ?? "");
    setBody(t?.body ?? "");
  };

  // Fresh on every open; the preselected template lands once the list is here.
  const initial = p.initialTemplateId ?? null;
  const haveTemplates = p.templates.length > 0;
  useEffect(() => {
    if (!p.open) return;
    pick(initial && haveTemplates ? initial : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.open, p.channel, initial, haveTemplates]);

  const renderedBody = useMemo(() => p.render(body), [p, body]);
  const renderedSubject = useMemo(() => p.render(subject), [p, subject]);
  const blanks = useMemo(() => [...new Set([...p.blanksIn(body), ...p.blanksIn(subject)])], [p, body, subject]);
  const segments = p.channel === "sms" && p.segmentsOf ? p.segmentsOf(renderedBody) : 0;
  const needsSubject = p.channel === "email" && !subject.trim();
  const address = p.to?.address ?? null;
  // A blank merge field would reach the recipient as "Hi ,". It blocks the send.
  const ready = Boolean(address && body.trim()) && !needsSubject && blanks.length === 0;

  const insert = (tag: string) => setBody((b) => (b ? `${b}${/\s$/.test(b) ? "" : " "}${tag}` : tag));

  return (
    <Modal
      open={p.open}
      title={p.title}
      hint={p.hint}
      onClose={p.onClose}
      width={760}
      footer={
        <>
          <Button onClick={p.onClose} disabled={p.sending}>Cancel</Button>
          <Button variant="primary" disabled={!ready || p.sending} onClick={() => p.onSend({ subject: renderedSubject.trim(), body: renderedBody.trim(), templateId })}>
            {p.sending ? "Sending" : "Send"}
          </Button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
        To <b style={{ color: "var(--ink-head)" }}>{p.to?.name ?? "nobody yet"}</b>
        {address ? `, ${address}` : `, no ${p.channel === "email" ? "email address" : "phone number"} on file`}
        {p.contextLine ? <>. {p.contextLine}</> : null}
      </p>

      {p.children}

      {haveTemplates && (
        <Labeled label="Template">
          <select value={templateId ?? ""} onChange={(e) => pick(e.target.value || null)} style={field}>
            <option value="">Blank</option>
            {p.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Labeled>
      )}

      {p.channel === "email" && (
        <Labeled label="Subject">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" style={field} />
        </Labeled>
      )}
      <Labeled label="Message">
        <textarea
          rows={7}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write it the way you would say it. Merge fields fill in below."
          style={textArea}
        />
      </Labeled>
      {p.chips.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {p.chips.map((c) => (
            <Button key={c.tag} variant="ghost" style={{ padding: "3px 9px", fontSize: 11.5 }} title={c.tag} onClick={() => insert(c.tag)}>+ {c.label}</Button>
          ))}
        </div>
      )}

      <div>
        <div className="eyebrow" style={{ marginBottom: 4 }}>Preview, as {p.to?.name ?? "the recipient"} reads it</div>
        <div style={{ border: "1px solid var(--hairline)", borderRadius: "var(--r-ctl)", background: "var(--card-2)", padding: "10px 12px", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {p.channel === "email" && renderedSubject && <div style={{ fontWeight: 700, color: "var(--ink-head)", marginBottom: 6 }}>{renderedSubject}</div>}
          {renderedBody || <span className="muted">Pick a template or start typing.</span>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: 12 }}>
        {p.channel === "sms" && p.segmentsOf && (
          <span className="num muted">{renderedBody.length} characters, {segments} segment{segments === 1 ? "" : "s"}</span>
        )}
        {p.channel === "sms" && renderedBody.length >= 320 && <Chip icon="⚠" tone="warn">Over 320 characters. Trim it.</Chip>}
        {blanks.length > 0 && <Chip icon="⚠" tone="warn">Nothing on file for {blanks.join(", ")}</Chip>}
        {needsSubject && <Chip icon="⚠" tone="warn">Email needs a subject</Chip>}
      </div>
      <Problem error={p.error} fallback="The message could not be sent." />
    </Modal>
  );
}
